import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { VehicleAccess, CreateVehicleDto, PaginatedResponse } from './access-record.service';

export interface VehicleFilter {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  accessRecordId?: number;
  licensePlate?: string;
  positionId?: number;
  establishmentId?: number;
  startDate?: string;
  endDate?: string;
  status?: 'active' | 'exited'
}

export interface VehicleStats {
  total: number;
  uniqueVehicles: number;
  byDay: { date: string; count: number }[];
}

@Injectable({
  providedIn: 'root'
})
export class VehicleAccessService {
  private readonly apiUrl = `${environment.apiUrl}/vehicle-access`;

  constructor(private http: HttpClient) {}

  /**
   * Adicionar veículo a um registro de acesso
   */
  create(accessRecordId: number, dto: CreateVehicleDto): Observable<VehicleAccess> {
    return this.http.post<VehicleAccess>(`${this.apiUrl}/record/${accessRecordId}`, dto).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Adicionar múltiplos veículos
   */
  createMany(accessRecordId: number, vehicles: CreateVehicleDto[]): Observable<VehicleAccess[]> {
    return this.http.post<VehicleAccess[]>(`${this.apiUrl}/record/${accessRecordId}/batch`, vehicles).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Listar veículos com paginação e filtros
   */
  findAll(filters?: VehicleFilter): Observable<PaginatedResponse<VehicleAccess>> {
    const params = this.buildParams(filters);
    return this.http.get<PaginatedResponse<VehicleAccess>>(this.apiUrl, { params }).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Buscar veículos por registro de acesso
   */
  findByAccessRecord(accessRecordId: number): Observable<VehicleAccess[]> {
    return this.http.get<VehicleAccess[]>(`${this.apiUrl}/record/${accessRecordId}`).pipe(
    
      catchError(this.handleError)
    );
  }

  /**
   * Buscar veículos por posto
   */
  findByPosition(positionId: number, limit: number = 50): Observable<VehicleAccess[]> {
    return this.http.get<VehicleAccess[]>(`${this.apiUrl}/position/${positionId}`, {
      params: new HttpParams().set('limit', limit.toString())
    }).pipe(
      
      catchError(this.handleError)
    );
  }

  /**
   * Buscar veículos por estabelecimento
   */
  findByEstablishment(establishmentId: number, limit: number = 50): Observable<VehicleAccess[]> {
    return this.http.get<VehicleAccess[]>(`${this.apiUrl}/establishment/${establishmentId}`, {
      params: new HttpParams().set('limit', limit.toString())
    }).pipe(
      
      catchError(this.handleError)
    );
  }

  /**
   * Buscar veículos por matrícula
   */
  findByLicensePlate(licensePlate: string, limit: number = 10): Observable<VehicleAccess[]> {
    return this.http.get<VehicleAccess[]>(`${this.apiUrl}/license-plate/${licensePlate}`, {
      params: new HttpParams().set('limit', limit.toString())
    }).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Buscar veículos de hoje
   */
  findTodayEntries(): Observable<VehicleAccess[]> {
    return this.http.get<VehicleAccess[]>(`${this.apiUrl}/today`).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Buscar veículo por ID
   */
  findOne(id: number): Observable<VehicleAccess> {
    return this.http.get<VehicleAccess>(`${this.apiUrl}/${id}`).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Atualizar veículo
   */
  update(id: number, dto: Partial<CreateVehicleDto>): Observable<VehicleAccess> {
    return this.http.put<VehicleAccess>(`${this.apiUrl}/${id}`, dto).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Remover veículo
   */
  remove(id: number): Observable<{ success: boolean; message: string }> {
    return this.http.delete<{ success: boolean; message: string }>(`${this.apiUrl}/${id}`).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Remover todos os veículos de um acesso
   */
  removeByAccessRecord(accessRecordId: number): Observable<{ success: boolean; message: string }> {
    return this.http.delete<{ success: boolean; message: string }>(`${this.apiUrl}/record/${accessRecordId}`).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Obter estatísticas de veículos
   */
  getStats(positionId?: number, startDate?: string, endDate?: string): Observable<VehicleStats> {
    let params = new HttpParams();
    if (positionId) params = params.set('positionId', positionId.toString());
    if (startDate) params = params.set('startDate', startDate);
    if (endDate) params = params.set('endDate', endDate);
    
    return this.http.get<VehicleStats>(`${this.apiUrl}/stats`, { params }).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Construir parâmetros HTTP
   */
  private buildParams(filters?: VehicleFilter): HttpParams {
    let params = new HttpParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params = params.append(key, value.toString());
        }
      });
    }
    
    return params;
  }

  /**
   * Tratamento de erros
   */
  private handleError(error: any): Observable<never> {
    console.error('Erro no VehicleAccessService:', error);
    const message = error.error?.message || error.message || 'Erro desconhecido';
    return throwError(() => new Error(message));
  }
}